import React, { useState } from "react";
import {
  Form,
  Space,
  Modal,
  Upload,
  Button,
  Table,
  message,
  Typography,
} from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { apiRequest } from "../../../../service/apiRequest";

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Sample template with exact columns requested
const sampleTemplate = [
  {
    product_name: "Wooden Chair",
    product_code: "HC-101",
    product_brand_name: "Royal Handicraft",
    category_name: "Furniture",
    unit_type: "Pcs",
    product_price: 1500,
    qty: 100,
  },
  {
    product_name: "Wooden Table",
    product_code: "HC-102",
    product_brand_name: "Royal Handicraft",
    category_name: "Furniture",
    unit_type: "Pcs",
    product_price: 3500,
    qty: 50,
  },
];

const ImportProductModal: React.FC<Props> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Download sample Excel file
  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet(sampleTemplate);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ProductTemplate");
    XLSX.writeFile(wb, "handicraft_product_import_template.xlsx");
  };

  // Upload and parse Excel
  const handleUpload = async (file: File) => {
    setLoading(true);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });

      if (!json.length) {
        message.warning("The Excel file is empty.");
        setParsedData([]);
        return false;
      }

      setParsedData(json);
      message.success("File loaded successfully!");
    } catch (err) {
      console.error(err);
      message.error("Invalid Excel format.");
      setParsedData([]);
    } finally {
      setLoading(false);
    }

    return false;
  };

  // Send parsed data to backend
  const handleImport = async () => {
    if (!parsedData.length) return;
    setLoading(true);
    try {
      const res = await apiRequest<any>({
        method: "post",
        url: "/import-handicraft-product",
        data: parsedData,
      });

      message.success(res?.message || "Handicraft products imported successfully!");
      setParsedData([]);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      message.error(
        err?.response?.data?.message || "Import failed. Check data format."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setParsedData([]);
    onClose();
  };

  return (
    <Modal
      title="Import Handicraft Products"
      width="85%"
      open={visible}
      onCancel={handleCancel}
      onOk={handleImport}
      okText="Import"
      okButtonProps={{ disabled: parsedData.length === 0 || loading, loading }}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item label="Step 1: Download Sample Template">
          <Space direction="vertical">
            <Text type="secondary">
              Download sample template with standard columns: product_name, product_code, product_brand_name, category_name, unit_type, product_price, qty
            </Text>
            <Button icon={<DownloadOutlined />} onClick={downloadSample}>
              Download Sample Template
            </Button>
          </Space>
        </Form.Item>

        <Form.Item label="Step 2: Upload Filled Excel File">
          <Space direction="vertical">
            <Text type="secondary">Upload filled Excel file (.xlsx)</Text>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={handleUpload}
              showUploadList={false}
              disabled={loading}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Upload Excel File
              </Button>
            </Upload>
          </Space>
        </Form.Item>
      </Form>

      {parsedData.length > 0 && (
        <>
          <Text style={{ marginTop: 12, display: "block", fontWeight: 600 }}>
            Preview of Imported Data ({parsedData.length} items):
          </Text>
          <Table
            dataSource={parsedData}
            rowKey={(_, i) => String(i)}
            size="small"
            scroll={{ y: 300 }}
            pagination={false}
            columns={[
              {
                title: "Product Name (product_name)",
                dataIndex: "product_name",
                key: "product_name",
                render: (v, r) => v || r["Product Name"] || "-",
              },
              {
                title: "Product Code (product_code)",
                dataIndex: "product_code",
                key: "product_code",
                render: (v, r) => v || r["Product Code"] || "-",
              },
              {
                title: "Brand Name (product_brand_name)",
                dataIndex: "product_brand_name",
                key: "product_brand_name",
                render: (v, r) => v || r["brand_name"] || r["Brand Name"] || r["Product Brand Name"] || "-",
              },
              {
                title: "Category Name (category_name)",
                dataIndex: "category_name",
                key: "category_name",
                render: (v, r) => v || r["parent_category"] || r["product_category"] || r["Category Name"] || "-",
              },
              {
                title: "Unit Type (unit_type)",
                dataIndex: "unit_type",
                key: "unit_type",
                render: (v, r) => v || r["unit"] || r["product_unit"] || r["Unit Type"] || "-",
              },
              {
                title: "Product Price (product_price)",
                dataIndex: "product_price",
                key: "product_price",
                render: (v, r) => v ?? r["price"] ?? "-",
              },
              {
                title: "Qty (qty)",
                dataIndex: "qty",
                key: "qty",
                render: (v, r) => v ?? r["product_qty"] ?? 0,
              },
            ]}
            style={{ marginTop: 8 }}
          />
        </>
      )}
    </Modal>
  );
};

export default ImportProductModal;
